/**
 * City / state / ZIP for proto3 onboarding.
 * Tries GPS plus reverse geocode, then falls back to IP geolocation.
 */

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const US_STATE_CODE_TO_NAME = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

function normalizeRegionName(regionRaw, countryCodeRaw) {
  const region = (regionRaw || '').trim();
  if (!region) return '';
  const countryCode = (countryCodeRaw || '').trim().toUpperCase();
  const upperRegion = region.toUpperCase();

  // Some providers return short state codes (e.g. "CA"), normalize to full name.
  if (countryCode === 'US' && US_STATE_CODE_TO_NAME[upperRegion]) {
    return US_STATE_CODE_TO_NAME[upperRegion];
  }
  return region;
}

function asCityRegion(cityRaw, regionRaw, countryCodeRaw) {
  const city = (cityRaw || '').trim();
  const region = normalizeRegionName(regionRaw, countryCodeRaw);
  if (!city && !region) return null;
  return { city: city || 'Unknown', region };
}

function pickCityRegionFromIpPayload(payload) {
  const preferredRegion = payload.region || payload.regionName || payload.state || payload.region_code || '';
  return asCityRegion(payload.city, preferredRegion, payload.countryCode || payload.country_code);
}

/**
 * City / state / ZIP for proto3 onboarding: GPS + BigDataCloud (postal when present), else IP (ipwho.is includes postal when available).
 * @param {{ signal?: AbortSignal, timeoutMs?: number }} [opts]
 * @returns {Promise<{ city: string, state: string, zip: string, latitude: number|null, longitude: number|null, source: 'gps'|'ip' }|null>}
 */
export async function getApproximateUserLocationForProto3(opts = {}) {
  const { signal, timeoutMs = 8000 } = opts;

  const digitsZip = (z) => String(z || '').replace(/\D/g, '').slice(0, 10);

  const tryGps = async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new Error('no geolocation');
    }
    const pos = await new Promise((resolve, reject) => {
      const id = window.setTimeout(() => reject(new Error('geo timeout')), timeoutMs);
      navigator.geolocation.getCurrentPosition(
        (p) => {
          window.clearTimeout(id);
          resolve(p);
        },
        (e) => {
          window.clearTimeout(id);
          reject(e);
        },
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 300_000 }
      );
    });
    const lat = pos.coords.latitude;
    const lon = pos.coords.longitude;
    const url = new URL('https://api.bigdatacloud.net/data/reverse-geocode-client');
    url.searchParams.set('latitude', String(lat));
    url.searchParams.set('longitude', String(lon));
    url.searchParams.set('localityLanguage', 'en');
    const res = await fetch(url.toString(), {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('BigDataCloud HTTP');
    const j = await parseJsonResponse(res);
    if (!j) throw new Error('BigDataCloud parse');
    const city = (j.locality || j.city || '').trim();
    const code = (j.principalSubdivisionCode || '').trim().toUpperCase();
    const sub = (j.principalSubdivision || '').trim();
    const zip = digitsZip(j.postcode || j.postalCode);
    const state = code.length === 2 ? code : sub;
    if (!city && !state) throw new Error('empty geocode');
    return {
      city: city || 'Unknown',
      state: state || '',
      zip: zip || '',
      latitude: lat,
      longitude: lon,
      source: 'gps',
    };
  };

  const tryIp = async () => {
    const res = await fetch('https://ipwho.is/', {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('ipwho HTTP');
    const j = await parseJsonResponse(res);
    if (!j || j.success !== true) throw new Error('ipwho payload');
    const picked = pickCityRegionFromIpPayload({
      city: j.city,
      region: j.region,
      region_code: j.region_code,
      country_code: j.country_code,
    });
    if (!picked) throw new Error('ipwho empty');
    const code = (j.region_code || '').trim().toUpperCase();
    const state = code.length === 2 ? code : picked.region || '';
    return {
      city: picked.city || 'Unknown',
      state: state || '',
      zip: digitsZip(j.postal),
      latitude: null,
      longitude: null,
      source: 'ip',
    };
  };

  try {
    return await tryGps();
  } catch {
    try {
      return await tryIp();
    } catch {
      return null;
    }
  }
}
